import clsx from 'clsx';
import React from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import OfferPrice from '@components/pages/Upgrade/OfferTable/OfferPrice';
import useEventTracking from '@hooks/useEventTracking';
import { subscribe_one_path, subscribe_path } from '@routes';
import { ClickTarget, click } from '@tracking';

import subscribeParams from '../utils/subscribeParams';
import { backgroundColorValues, marginValues, paddingValues } from './constants';
import type { SubscribeButtonBlockProps } from './interfaces';

const SubscribeButtonBlock = ({ config, offer, redirectTo }: SubscribeButtonBlockProps) => {
  const {
    analytics_location: analyticsLocation,
    background_color: backgroundColor,
    custom_subscription_description: customSubscriptionDescription = '',
    css_classes: cssClasses,
    display_subscription_description: displaySubscriptionDescription = false,
    display_price_breakdown: displayPriceBreakdown = false,
    is_dark_mode: isDarkMode = false,
    margin,
    padding,
    pro_subscription: isProSubscription,
    subscribe_one: isSubscribeOne,
    text: buttonText,
  } = config;

  const { trackEvent } = useEventTracking({
    location: analyticsLocation,
  });

  const Button = offer?.isPro || isProSubscription ? ProButton : PrimaryButton;

  const handleClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.Subscribe,
        clickText: buttonText,
      })
    );
  };

  return (
    <article
      className={clsx('subscribe-button-block', {
        'text-white': isDarkMode,
      })}
    >
      {displaySubscriptionDescription && (
        <p className="subscription-description">
          {customSubscriptionDescription || offer?.description}
        </p>
      )}
      {displayPriceBreakdown && (
        <OfferPrice
          billingFrequencyClassName={clsx('!text-[24px]', { '!text-white': isDarkMode })}
          className="md:justify-start"
          currentPriceClassName={clsx('!text-[24px] !font-bold', { '!text-white': isDarkMode })}
          offer={offer}
          originalPriceClassName={clsx('!text-[24px] font-light not-italic', {
            '!text-grayscale-beige': isDarkMode,
          })}
          upgradeSavings={null}
        />
      )}

      <Button
        as="link"
        className={clsx(
          'subscribe-button-block',
          backgroundColorValues[backgroundColor],
          cssClasses,
          marginValues[margin],
          paddingValues[padding],
          {
            'mt-16 md:mt-8': displaySubscriptionDescription || displayPriceBreakdown,
          }
        )}
        href={
          isSubscribeOne
            ? subscribe_one_path(subscribeParams({ offerCode: offer?.code, redirectTo }))
            : subscribe_path(subscribeParams({ offerCode: offer?.code, redirectTo }))
        }
        onClickCapture={handleClick}
      >
        {buttonText}
      </Button>
    </article>
  );
};

export default SubscribeButtonBlock;
